import { useState } from 'preact/hooks';
import {
  savedPlaylists,
  currentPlaylist,
  playlistBuilding,
  playlistError,
  buildPlaylist,
  setPlaylistIndex,
  deletePlaylist,
  type Playlist,
  type PlaylistItem,
} from '../state/playlist-store.js';
import { route } from '../state/store.js';

type Props = {
  onClose: () => void;
  onPick: (book: string, chapter: number) => void;
};

const CATEGORIES = [
  { id: 'people', label: 'People', icon: '👤', prompt: 'Stories of biblical people and their faith journeys' },
  { id: 'events', label: 'Events', icon: '⚡', prompt: 'Major events and turning points in scripture' },
  { id: 'evangelism', label: 'Evangelism', icon: '🌱', prompt: 'Verses for sharing the gospel with seekers' },
  { id: 'discipleship', label: 'Discipleship', icon: '📚', prompt: 'Core passages for growing in faith and following Jesus' },
  { id: 'themes', label: 'Themes', icon: '🔍', prompt: 'Explore a specific theme or topic in scripture' },
] as const;

type Tab = 'build' | 'saved';

export function PlaylistPanel({ onClose, onPick }: Props) {
  const [tab, setTab] = useState<Tab>('build');
  const [prompt, setPrompt] = useState('');
  const [selectedCat, setSelectedCat] = useState<string | null>(null);

  async function handleBuild() {
    if (!prompt.trim()) return;
    const result = await buildPlaylist(prompt.trim());
    if (result) setTab('build');
  }

  return (
    <div class="playlist-panel-backdrop" onClick={onClose} role="dialog" aria-modal="true">
      <div class="playlist-panel" onClick={(e) => e.stopPropagation()}>
        <header class="playlist-panel-header">
          <button class="playlist-panel-back" onClick={onClose} aria-label="Close">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
              <path d="M12.5 4.5L7.5 10l5 5.5" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          <span class="playlist-panel-title">AI Playlist</span>
          <button
            class={`playlist-panel-tab${tab === 'saved' ? ' is-active' : ''}`}
            onClick={() => setTab('saved')}
          >
            Saved
          </button>
        </header>

        {tab === 'saved' ? (
          <SavedView
            onPick={onPick}
            onClose={onClose}
            onSwitchBuild={() => setTab('build')}
          />
        ) : (
          <div class="playlist-build">
            <div class="playlist-cats">
              {CATEGORIES.map((cat) => (
                <button
                  key={cat.id}
                  class={`playlist-cat${selectedCat === cat.id ? ' is-active' : ''}`}
                  onClick={() => {
                    setSelectedCat(cat.id);
                    setPrompt(cat.prompt);
                  }}
                >
                  {cat.icon} {cat.label}
                </button>
              ))}
            </div>

            <div class="playlist-input-row">
              <textarea
                class="playlist-input"
                placeholder="What do you want to read about? e.g. 'Paul's missionary journeys' or 'verses about anxiety'"
                value={prompt}
                onInput={(e) => setPrompt((e.target as HTMLTextAreaElement).value)}
                rows={3}
              />
              <button
                class="playlist-build-btn"
                onClick={() => void handleBuild()}
                disabled={!prompt.trim() || playlistBuilding.value}
              >
                {playlistBuilding.value ? 'Building…' : 'Build Playlist'}
              </button>
            </div>

            {playlistError.value && (
              <p class="playlist-error">{playlistError.value}</p>
            )}

            {currentPlaylist.value && (
              <ActivePlaylistView onPick={onPick} onClose={onClose} />
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function SavedView({
  onPick,
  onClose,
  onSwitchBuild,
}: {
  onPick: (book: string, chapter: number) => void;
  onClose: () => void;
  onSwitchBuild: () => void;
}) {
  const playlists = savedPlaylists.value;

  if (playlists.length === 0) {
    return (
      <div class="playlist-saved-empty">
        <p class="playlist-saved-empty-text">No saved playlists yet.</p>
        <button class="playlist-saved-empty-cta" onClick={onSwitchBuild}>
          Build your first playlist →
        </button>
      </div>
    );
  }

  return (
    <ul class="playlist-saved-list">
      {playlists.map((p) => (
        <SavedPlaylistRow key={p.id} playlist={p} onPick={onPick} onClose={onClose} />
      ))}
    </ul>
  );
}

function SavedPlaylistRow({
  playlist,
  onPick,
  onClose,
}: {
  playlist: Playlist;
  onPick: (book: string, chapter: number) => void;
  onClose: () => void;
}) {
  function handleLoad() {
    setPlaylistIndex(0);
    const first = playlist.items[0];
    if (first) {
      route.value = { book: first.book, chapter: first.chapter };
      onPick(first.book, first.chapter);
      onClose();
    }
  }

  const date = new Date(playlist.createdAt).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

  return (
    <li class="playlist-saved-row">
      <div class="playlist-saved-meta">
        <span class="playlist-saved-name">{playlist.name}</span>
        <span class="playlist-saved-info">{playlist.items.length} passages · {date}</span>
      </div>
      <div class="playlist-saved-actions">
        <button class="playlist-saved-load" onClick={handleLoad}>
          Load
        </button>
        <button
          class="playlist-saved-delete"
          aria-label={`Delete playlist ${playlist.name}`}
          onClick={() => deletePlaylist(playlist.id)}
        >
          ×
        </button>
      </div>
    </li>
  );
}

function ActivePlaylistView({
  onPick,
  onClose,
}: {
  onPick: (book: string, chapter: number) => void;
  onClose: () => void;
}) {
  const state = currentPlaylist.value;
  if (!state) return null;
  const { playlist, index } = state;

  return (
    <div class="playlist-active">
      <div class="playlist-active-header">
        <span class="playlist-active-name">{playlist.name}</span>
        <span class="playlist-active-count">{playlist.items.length} passages</span>
      </div>
      <div class="playlist-active-items">
        {playlist.items.map((item, i) => (
          <button
            key={item.ref}
            class={`playlist-item${i === index ? ' is-current' : ''}`}
            onClick={() => {
              setPlaylistIndex(i);
              onPick(item.book, item.chapter);
              onClose();
            }}
          >
            <span class="playlist-item-surface">{item.surface}</span>
            <span class="playlist-item-why">{item.why}</span>
            {i === index && <span class="playlist-item-now" aria-label="Now playing">▶</span>}
          </button>
        ))}
      </div>
    </div>
  );
}

export function PlaylistChip({ onOpenPanel }: { onOpenPanel: () => void }) {
  const state = currentPlaylist.value;
  if (!state) return null;
  const { playlist, index } = state;
  const item = playlist.items[index];
  if (!item) return null;
  return (
    <button class="playlist-chip" onClick={onOpenPanel}>
      <span class="playlist-chip-label">🎵 {playlist.name}</span>
      <span class="playlist-chip-pos">{index + 1}/{playlist.items.length}</span>
    </button>
  );
}
